import type DifferentiationMode from "../core/DifferentiationMode";
import type ExplainDerivativeBuildOptions from "./ExplainDerivativeBuildOptions";
import type ExplainDerivativeItem from "./ExplainDerivativeItem";
import type ExplainDerivativeItemType from "./ExplainDerivativeItemType";
import type ExplainDerivativeType from "./ExplainDerivativeType";
import type MathLabelPartType from "./MathLabelPartType";
import { formatNumber } from "./NumberFormatter";

type ChainRuleType = "raw" | "previousDerivativesReplaced" | "allReplaced";

const buildExplainDerivativeItems = (
  explainDerivativeType: ExplainDerivativeType,
  options: ExplainDerivativeBuildOptions,
): ExplainDerivativeItem[] => {
  switch (explainDerivativeType) {
    case "someValueBecauseChainRule": {
      return buildChainRuleItems(options);
    }
    case "oneBecauseFEqualsX": {
      return buildOneBecauseFEqualsXItems(options);
    }
    case "zeroBecauseFNotDependsOnX": {
      return buildZeroBecauseFNotDependsOnXItems(options);
    }
    case "zeroBecauseXIsConstant": {
      return buildZeroBecauseXIsConstantItems(options);
    }
  }
};

const buildOneBecauseFEqualsXItems = (
  options: ExplainDerivativeBuildOptions,
): ExplainDerivativeItem[] => {
  const nodeName = getNodeName(options.nodeId, options.nodeIdToNames);
  return [
    buildCalculateDerivativeItem(options),
    {
      type: "oneBecauseFEqualsX",
      descriptionParts: [
        {
          type: "text",
          id: "which",
          text: "Which is ",
        },
        {
          type: "latex",
          id: "value",
          latex: formatValue(options.nodeDerivative, options),
        },
        {
          type: "text",
          id: "because",
          text: " because the derivative target is ",
        },
        {
          type: "latex",
          id: "node",
          latex: nodeName,
        },
        {
          type: "text",
          id: "colon",
          text: ":",
        },
      ],
      latex: `\\displaystyle ${formatValue(options.nodeDerivative, options)}`,
    },
  ];
};

const buildZeroBecauseFNotDependsOnXItems = (
  options: ExplainDerivativeBuildOptions,
): ExplainDerivativeItem[] => {
  return [
    buildCalculateDerivativeItem(options),
    {
      type: "zeroBecauseFNotDependsOnX",
      descriptionParts: buildNotDependingDescription(options),
      latex: `\\displaystyle ${formatValue(options.nodeDerivative, options)}`,
    },
  ];
};

/**
 * Explains why a derivative is zero, naming the right end of the dependency.
 *
 * The two modes differentiate opposite ways round, so the same zero has
 * opposite reasons:
 * - Reverse mode computes d(target)/d(node), which is zero when the target
 *   does not depend on the node.
 * - Forward mode computes d(node)/d(target), which is zero when the node
 *   does not depend on the target.
 *
 * Saying it the wrong way round states something false: with `a_1 = v_1 + v_2`
 * and `a_1` as the target, d(v_1)/d(a_1) is zero, yet `a_1` very much does
 * depend on `v_1`.
 *
 * @param options Build options.
 * @returns Description parts for the zero derivative.
 */
const buildNotDependingDescription = (
  options: ExplainDerivativeBuildOptions,
): MathLabelPartType[] => {
  const nodeName = getNodeName(options.nodeId, options.nodeIdToNames);
  const prefixParts: MathLabelPartType[] = [
    {
      type: "text",
      id: "which",
      text: "Which is ",
    },
    {
      type: "latex",
      id: "value",
      latex: formatValue(options.nodeDerivative, options),
    },
  ];

  if (options.differentiationMode === "REVERSE") {
    return [
      ...prefixParts,
      {
        type: "text",
        id: "because",
        text: " because the derivative target doesn't depend on ",
      },
      {
        type: "latex",
        id: "node",
        latex: nodeName,
      },
      {
        type: "text",
        id: "colon",
        text: ":",
      },
    ];
  }

  return [
    ...prefixParts,
    {
      type: "text",
      id: "because",
      text: " because ",
    },
    {
      type: "latex",
      id: "node",
      latex: nodeName,
    },
    {
      type: "text",
      id: "colon",
      text: " doesn't depend on the derivative target:",
    },
  ];
};

const buildZeroBecauseXIsConstantItems = (
  options: ExplainDerivativeBuildOptions,
): ExplainDerivativeItem[] => {
  const nodeName = getNodeName(options.nodeId, options.nodeIdToNames);
  return [
    buildCalculateDerivativeItem(options),
    {
      type: "zeroBecauseXIsConstant",
      descriptionParts: [
        {
          type: "text",
          id: "which",
          text: "Which is ",
        },
        {
          type: "latex",
          id: "value",
          latex: formatValue(options.nodeDerivative, options),
        },
        {
          type: "text",
          id: "because",
          text: " because ",
        },
        {
          type: "latex",
          id: "node",
          latex: nodeName,
        },
        {
          type: "text",
          id: "is",
          text: " is a constant:",
        },
      ],
      latex: `\\displaystyle ${formatValue(options.nodeDerivative, options)}`,
    },
  ];
};

const buildChainRuleItems = (
  options: ExplainDerivativeBuildOptions,
): ExplainDerivativeItem[] => {
  return [
    buildCalculateDerivativeItem(options),
    {
      type: "useChainRule",
      descriptionParts: [
        {
          type: "text",
          id: "mainText",
          text: "Use the chain rule:",
        },
      ],
      latex: buildChainRuleLatex("raw", options),
    },
    {
      type: "previousDerivativesReplaced",
      descriptionParts: buildPreviousDerivativesReplacedDescription(options),
      latex: buildChainRuleLatex("previousDerivativesReplaced", options),
    },
    {
      type: "allReplaced",
      descriptionParts: buildAllReplacedDescription(
        options.differentiationMode,
      ),
      latex: buildChainRuleLatex("allReplaced", options),
    },
    {
      type: "equalTo",
      descriptionParts: [
        {
          type: "text",
          id: "mainText",
          text: "Which is equal to:",
        },
      ],
      latex: `\\displaystyle ${formatValue(options.nodeDerivative, options)}`,
    },
  ];
};

const buildCalculateDerivativeItem = (
  options: ExplainDerivativeBuildOptions,
): ExplainDerivativeItem => {
  return {
    type: "calculatePartialDerivative",
    descriptionParts: [
      {
        type: "text",
        id: "mainText",
        text: "Calculate the partial derivative:",
      },
    ],
    latex: buildPartialDerivativeSubject(options),
  };
};

const buildPartialDerivativeSubject = (
  options: ExplainDerivativeBuildOptions,
): string => {
  const nodeName = getNodeName(options.nodeId, options.nodeIdToNames);
  const targetNodeName = getNodeName(
    options.targetNodeId,
    options.nodeIdToNames,
  );
  const derivative =
    options.differentiationMode === "REVERSE"
      ? buildPartialDerivativeLatex(targetNodeName, nodeName)
      : buildPartialDerivativeLatex(nodeName, targetNodeName);
  return `\\displaystyle ${derivative}`;
};

const buildPreviousDerivativesReplacedDescription = (
  options: ExplainDerivativeBuildOptions,
): MathLabelPartType[] => {
  const side = options.differentiationMode === "REVERSE" ? "right" : "left";
  const targetNodeName = getNodeName(
    options.targetNodeId,
    options.nodeIdToNames,
  );
  const previousDerivativeTerms: MathLabelPartType[] =
    options.chainRuleTerms.map((chainRuleTerm, index) => {
      const neighborNodeName = getNodeName(
        chainRuleTerm.neighborNodeId,
        options.nodeIdToNames,
      );
      let derivative: string;
      if (options.differentiationMode === "REVERSE") {
        derivative = buildPartialDerivativeLatex(
          targetNodeName,
          neighborNodeName,
        );
      } else {
        derivative = buildPartialDerivativeLatex(
          neighborNodeName,
          targetNodeName,
        );
      }
      return {
        type: "latexLink",
        id: `chainRuleTerm-${chainRuleTerm.neighborNodeId}-${index}`,
        latex: derivative,
        href: chainRuleTerm.neighborNodeId,
      };
    });

  const commaListParts: MathLabelPartType[] = [];
  previousDerivativeTerms.forEach((previousDerivativeTerm, index) => {
    if (index > 0) {
      commaListParts.push({
        type: "text",
        id: `and-${index}`,
        text: index === previousDerivativeTerms.length - 1 ? " and " : ", ",
      });
    }
    commaListParts.push(previousDerivativeTerm);
  });

  return [
    {
      type: "text",
      id: "replace",
      text: `Replace the ${side}-hand side `,
    },
    ...commaListParts,
    {
      type: "text",
      id: "with",
      text: " with the previously calculated value(s):",
    },
  ];
};

const buildAllReplacedDescription = (
  differentiationMode: DifferentiationMode,
): MathLabelPartType[] => {
  const side = differentiationMode === "REVERSE" ? "left" : "right";
  return [
    {
      type: "text",
      id: "mainText",
      text: `Calculate the ${side}-hand side derivative(s):`,
    },
  ];
};

const buildChainRuleLatex = (
  chainRuleType: ChainRuleType,
  options: ExplainDerivativeBuildOptions,
): string => {
  const multiplicationTerms = buildChainRuleMultiplicationTerms(
    chainRuleType,
    options,
  );
  const expression = multiplicationTerms.join("+");
  return `\\displaystyle =${expression}`;
};

const buildChainRuleMultiplicationTerms = (
  chainRuleType: ChainRuleType,
  options: ExplainDerivativeBuildOptions,
): string[] => {
  const nodeName = getNodeName(options.nodeId, options.nodeIdToNames);
  const targetNodeName = getNodeName(
    options.targetNodeId,
    options.nodeIdToNames,
  );
  return options.chainRuleTerms.map((chainRuleTerm) => {
    const neighborNodeName = getNodeName(
      chainRuleTerm.neighborNodeId,
      options.nodeIdToNames,
    );
    let derivative1: string;
    let derivative2: string;
    if (options.differentiationMode === "REVERSE") {
      derivative1 =
        chainRuleType === "allReplaced"
          ? formatValue(chainRuleTerm.derivativeRegardingCurrent, options)
          : buildPartialDerivativeLatex(neighborNodeName, nodeName);
      derivative2 =
        chainRuleType === "raw"
          ? buildPartialDerivativeLatex(targetNodeName, neighborNodeName)
          : formatValue(chainRuleTerm.derivativeRegardingTarget, options);
    } else {
      derivative1 =
        chainRuleType === "raw"
          ? buildPartialDerivativeLatex(neighborNodeName, targetNodeName)
          : formatValue(chainRuleTerm.derivativeRegardingTarget, options);
      derivative2 =
        chainRuleType === "allReplaced"
          ? formatValue(chainRuleTerm.derivativeRegardingCurrent, options)
          : buildPartialDerivativeLatex(nodeName, neighborNodeName);
    }
    return `${derivative1} \\cdot ${derivative2}`;
  });
};

/**
 * Rounds a value for the explanation.
 *
 * Every number the explanation prints goes through here, so that the working
 * reads the same as the nodes it's about: a factor copied off a node and the
 * factor it's multiplied by here are the same string.
 *
 * Rounding is display only. The chain rule itself is worked out by the core
 * graph from the exact values, so a rounded explanation never becomes a
 * rounded result.
 *
 * @param value The value as the core graph holds it.
 * @param options Build options.
 * @returns The value to print.
 */
const formatValue = (
  value: string,
  options: ExplainDerivativeBuildOptions,
): string => {
  return formatNumber(value, options.decimalPlaces);
};

const buildPartialDerivativeLatex = (
  numerator: string,
  denominator: string,
): string => {
  return `\\frac{\\partial{${numerator}}}{\\partial{${denominator}}}`;
};

const getNodeName = (
  nodeId: string,
  nodeIdToNames: ReadonlyMap<string, string>,
): string => {
  const name = nodeIdToNames.get(nodeId);
  if (name === undefined) {
    throw new Error(`The nodeIdToNames mapping should have node ID ${nodeId}`);
  }
  return name;
};

/**
 * Whether an item states the chain rule without giving away any of its numbers.
 *
 * These are the only items the manual walkthrough shows while its answers are
 * hidden. Every other item carries a value the graph worked out, and any of
 * them can hand the answer over on its own:
 * - the substituted chain rule reads `-0.578 * 1` when there is a single term,
 *   which is the answer already
 * - the neighbor derivatives it substitutes are the answers of the nodes the
 *   user is meant to have worked out first
 * - the last item is the value itself
 *
 * @param type Type of the item.
 * @returns Whether it is safe to show with the answers hidden.
 */
const isSymbolicExplainDerivativeItem = (
  type: ExplainDerivativeItemType,
): boolean => {
  return type === "calculatePartialDerivative" || type === "useChainRule";
};

export { buildExplainDerivativeItems, isSymbolicExplainDerivativeItem };
